import React, { useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { useCookies } from 'react-cookie'

import { store } from 'app-redux/store'

import SaveService from 'services/save.service'
import CardService from 'services/card.service'
import ToastService from 'services/toast.service'

import { CardType } from 'types/deck'
import { PRICE_CODES } from 'types/active'

import BasicCard from 'components/card/BasicCard'

import Prices from 'components/card/controlsAndInfo/prices'
import PhatDropdown from 'components/formElements/PhatDropdown'
import CheckboxDropdown from 'components/formElements/CheckboxDropdown'
import Checkbox from 'components/formElements/Checkbox'
import PhatButton from 'components/formElements/PhatButton'
import SetDropdown from 'components/cardSearchPanel/searchFormElements/setDropdown'

import { getPriceStringAndUrl } from 'utils/Price'

import styles from './optimizeCards.module.scss'

type Props = {
  onClose: () => void
}

export type OptimizeResults = {
  fromCard: CardType
  toCard: CardType
  enabled: boolean
}

type Preferences =
  | 'preferCheapest'
  | 'preferFancy'
  | 'preferExpensive'
  | 'preferOld'
  | 'preferNew'
  | 'preferRetroFrames'
type ForceCardsToState = 'auto' | 'normal' | 'foil'
type Inclusions = 'includeGoldBorder' | 'includeDigital'
type Exclusions =
  | 'excludeNormal'
  | 'excludeInverted'
  | 'excludeExtendedArt'
  | 'excludeFullArt'
  | 'excludeBorderless'
  | 'excludeMasterpiece'
  | 'excludeShowcase'
  | 'excludePromo'
  | 'excludeUb'
  | 'excludeWhiteBorder'

export type Options = {
  preference: Preferences
  setPreference: string[] | null

  inclusions: Inclusions[]
  exclusions: Exclusions[]

  limitToCollection: boolean
  forceCardsToState: ForceCardsToState
}

const defaultOptions: Options = {
  preference: 'preferCheapest',
  setPreference: null,

  inclusions: [],
  exclusions: [],

  limitToCollection: false,
  forceCardsToState: 'auto',
}

const LOCAL_STORAGE_KEY = 'optimizeCardsOptions'

const OptimizeCards = ({ onClose }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [options, setOptions] = useLocalStorage<Options>(LOCAL_STORAGE_KEY, defaultOptions)

  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(new Array<OptimizeResults>())

  const handleOptimization = async () => {
    const state = store.getState()
    const multiSelectedIds = Object.keys(state.active.multiSelectedIds)
    const cardMap = state.deck.cardMap
    const defaultPriceSource = state.active.priceSource[0]

    const priceSource = PRICE_CODES[defaultPriceSource]

    const oracleIds = multiSelectedIds.map(id => cardMap[id].oracleCardId)

    setLoading(true)

    try {
      const { cards, next } = await CardService.optimizeCards({
        oracleIds,
        priceSource,
        // Note preferCheapest is the default and is a no-op if it is set, not other flags are set. It's the default
        // preferCheapest: options.preference === 'preferCheapest', // Unnecessary - it's the default
        preferExpensive: options.preference === 'preferExpensive',
        preferFancyCardLook: options.preference === 'preferFancy',
        preferOld: options.preference === 'preferOld',
        preferNew: options.preference === 'preferNew',
        preferRetroFrames: options.preference === 'preferRetroFrames',
        excludeNormal: options.exclusions.includes('excludeNormal'),
        excludeInverted: options.exclusions.includes('excludeInverted'),
        excludeExtendedArt: options.exclusions.includes('excludeExtendedArt'),
        excludeFullArt: options.exclusions.includes('excludeFullArt'),
        excludeBorderless: options.exclusions.includes('excludeBorderless'),
        excludeMasterpiece: options.exclusions.includes('excludeMasterpiece'),
        excludeShowcase: options.exclusions.includes('excludeShowcase'),
        excludePromo: options.exclusions.includes('excludePromo'),
        excludeUb: options.exclusions.includes('excludeUb'),
        excludeWhiteBorder: options.exclusions.includes('excludeWhiteBorder'),
        includeDigital: options.inclusions.includes('includeDigital'),
        includeGoldBorder: options.inclusions.includes('includeGoldBorder'),
        limitToCollection: options.limitToCollection,
        preferEditions: options.setPreference,
      })

      if (cards.length === 0)
        ToastService.create(
          'Optimize successful. No results returned given the settings you provided.',
          'Optimize Service',
          'success',
        )

      if (next)
        ToastService.create(
          'Skipping some cards to optimize. We can only optimize up to 150 cards at a time, if you want to do more than that you will need to do so in multiple waves.',
          'Deck page',
          'warning',
        )

      const results = new Array<OptimizeResults>()

      const upperCaseSetPreference = options.setPreference
        ? options.setPreference.map(edition => edition.toUpperCase())
        : []

      for (const id of multiSelectedIds) {
        const fromCard = cardMap[id]
        const toCard = cards.find(c => c.oracleCardId === fromCard.oracleCardId)

        if (!fromCard || !toCard) continue
        if (fromCard.cardId === toCard.cardId) continue

        const { price: fromPrice } = getPriceStringAndUrl(fromCard, defaultPriceSource, fromCard.modifier !== 'Normal')
        const { price: toPrice } = getPriceStringAndUrl(toCard, defaultPriceSource, toCard.modifier !== 'Normal')

        if (
          ['preferCheapest', 'preferExpensive'].includes(options.preference) &&
          fromPrice === toPrice &&
          options.setPreference &&
          upperCaseSetPreference.includes(fromCard.setCode.toUpperCase())
        )
          continue

        const toCardHasNormal = toCard.options.includes('Normal')
        const toCardHasFoil = toCard.options.includes('Foil')
        // const toCardHasEtched = toCard.options.includes('Etched')

        let modifier = toCard.modifier

        if (options.forceCardsToState === 'normal' && toCardHasNormal) modifier = 'Normal'
        if (options.forceCardsToState === 'foil' && toCardHasFoil) modifier = 'Foil'

        results.push({
          fromCard,
          toCard: { ...toCard, modifier },
          enabled: true,
        })
      }

      setResults(results)
    } catch {
      ToastService.create('Unable to optimize cards', 'Deck Page', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleEnabled = (resultIndex: number) => {
    const updatedResults = [...results]

    updatedResults[resultIndex].enabled = !updatedResults[resultIndex].enabled

    setResults(updatedResults)
  }

  const handleReplaceCards = () => {
    const enabledOptions = results.filter(r => r.enabled)
    const updatedCards: CardType[] = enabledOptions.map(option => ({
      ...option.toCard,
      // If the modifier can stay the same, we leave it the same. Otherwise we'll change it to whatever the new cards default is
      modifier: option.toCard.options.includes(option.fromCard.modifier)
        ? option.fromCard.modifier
        : option.toCard.options[0],
      id: option.fromCard.id,
      deckRelationId: option.fromCard.deckRelationId,
      qty: option.fromCard.qty,
      colorLabel: option.fromCard.colorLabel,
      companion: option.fromCard.companion,
      categories: option.fromCard.categories,
      customCmc: option.fromCard.customCmc,
    }))

    SaveService.save(updatedCards)
      .then(() => {
        setResults([])
        onClose()
      })
      .catch(() => ToastService.create('Unable to replace cards', 'Deck Page', 'error'))
  }

  const totalEnabledCards = results.filter(r => r.enabled).length

  return (
    <div>
      <div className={styles.form}>
        <PhatDropdown
          inlineHeader
          header="Preferred Printing"
          value={options.preference}
          options={[
            {
              label: 'Cheapest',
              id: 'preferCheapest',
              onClick: () => setOptions({ ...options, preference: 'preferCheapest' }),
            },
            {
              label: 'Most expensive',
              id: 'preferExpensive',
              onClick: () => setOptions({ ...options, preference: 'preferExpensive' }),
            },
            {
              label: 'Oldest',
              id: 'preferOld',
              onClick: () => setOptions({ ...options, preference: 'preferOld' }),
            },
            {
              label: 'Newest',
              id: 'preferNew',
              onClick: () => setOptions({ ...options, preference: 'preferNew' }),
            },
            {
              label: 'Fancy',
              id: 'preferFancy',
              onClick: () => setOptions({ ...options, preference: 'preferFancy' }),
            },
            {
              label: 'Retro Frames',
              id: 'preferRetroFrames',
              onClick: () => setOptions({ ...options, preference: 'preferRetroFrames' }),
            },
          ]}
        />
        <CheckboxDropdown
          header="Exclusions"
          placeholder="None"
          includeSelectAllNoneOptions
          value={options.exclusions}
          onChange={exclusions => setOptions({ ...options, exclusions })}
          options={[
            {
              label: 'Exclude normal',
              id: 'excludeNormal',
            },
            {
              label: 'Exclude Universes Beyond',
              id: 'excludeUb',
            },
            {
              label: 'Exclude inverted',
              id: 'excludeInverted',
            },
            {
              label: 'Exclude extended art',
              id: 'excludeExtendedArt',
            },
            {
              label: 'Exclude full art',
              id: 'excludeFullArt',
            },
            {
              label: 'Exclude borderless',
              id: 'excludeBorderless',
            },
            {
              label: 'Exclude masterpiece',
              id: 'excludeMasterpiece',
            },
            {
              label: 'Exclude showcase',
              id: 'excludeShowcase',
            },
            {
              label: 'Exclude promo',
              id: 'excludePromo',
            },
            {
              label: 'Exclude white border',
              id: 'excludeWhiteBorder',
            },
          ]}
        />

        <CheckboxDropdown
          header="Inclusions"
          includeSelectAllNoneOptions
          placeholder="None"
          value={options.inclusions}
          onChange={inclusions => setOptions({ ...options, inclusions })}
          options={[
            {
              label: 'Include digital',
              id: 'includeDigital',
            },
            {
              label: 'Include gold border',
              id: 'includeGoldBorder',
            },
          ]}
        />

        <SetDropdown
          header="Limit to edition"
          value={options.setPreference || []}
          onChange={editions => setOptions({ ...options, setPreference: editions })}
        />

        {!!userId && (
          <div className={styles.options}>
            <Checkbox
              checked={options.limitToCollection}
              className={styles.option}
              label="Limit to my collection"
              onChange={() => setOptions({ ...options, limitToCollection: !options.limitToCollection })}
            />
          </div>
        )}
        <h4>Card Options</h4>
        <p>
          These settings won't change the optimization results. This setting will force the results to change their
          foil/non-foil status of cards if those settings have a foil/non-foil version available.
        </p>

        <PhatDropdown
          header="Force Cards to State"
          inlineHeader
          value={options.forceCardsToState}
          options={[
            {
              label: 'Use existing foil/non-foil status',
              id: 'auto',
              onClick: () => setOptions({ ...options, forceCardsToState: 'auto' }),
            },
            {
              label: 'Force all cards to non-foil',
              id: 'normal',
              onClick: () => setOptions({ ...options, forceCardsToState: 'normal' }),
            },
            {
              label: 'Force all cards to foil',
              id: 'foil',
              onClick: () => setOptions({ ...options, forceCardsToState: 'foil' }),
            },
          ]}
        />
      </div>
      <div className={styles.controls}>
        <PhatButton color="blue" onClick={handleOptimization}>
          Show Optimize Printings
        </PhatButton>
        <PhatButton disabled={!totalEnabledCards} color="green" onClick={handleReplaceCards}>
          Overwrite {totalEnabledCards} Cards
        </PhatButton>
      </div>
      {!loading && (
        <div className={styles.cards}>
          {results.map((optimizationCouple, index) => {
            if (!optimizationCouple?.fromCard || !optimizationCouple?.toCard) return null

            return (
              <div key={index} className={styles.cardGroup}>
                <div className={styles.cardWrapper}>
                  <BasicCard
                    onClick={() => handleToggleEnabled(index)}
                    card={optimizationCouple.fromCard}
                    className={`${styles.card} ${optimizationCouple.enabled ? styles.disabled : ''}`}
                  />
                  <Prices card={optimizationCouple.fromCard} />
                </div>

                <Checkbox checked={optimizationCouple.enabled} onChange={() => handleToggleEnabled(index)} />
                <div className={styles.cardWrapper}>
                  <BasicCard
                    onClick={() => handleToggleEnabled(index)}
                    card={optimizationCouple.toCard}
                    className={`${styles.card} ${optimizationCouple.enabled ? '' : styles.disabled}`}
                    imageClassName={styles.basicCard}
                  />
                  <Prices card={optimizationCouple.toCard} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default OptimizeCards
